/**
 * Feed Browse View — manual-import feeds only.
 *
 * Shows articles with status = 'browsing'. Editor can:
 * - Select → triggers content fetch + rewrite pipeline
 * - Dismiss → permanent dismiss (never resurfaces)
 *
 * If content fetch fails after selection, the Worker sets status = content_incomplete.
 * A refresh will show the article in the Review Queue under "Incomplete".
 */
import { useEffect, useState, useCallback } from "react";
import { api, type BrowseArticle, type Feed } from "../lib/api.ts";
import { relativeTime, truncate } from "../lib/utils.ts";

export default function FeedBrowse() {
  const [articles, setArticles]     = useState<BrowseArticle[]>([]);
  const [feeds, setFeeds]           = useState<Feed[]>([]);
  const [selectedFeed, setSelectedFeed] = useState<string>("");
  const [loading, setLoading]       = useState(true);
  const [actionMap, setActionMap]   = useState<Record<number, "selecting" | "dismissing" | "done">>({});

  const loadData = useCallback(() => {
    setLoading(true);
    const params: Record<string, string> = {};
    if (selectedFeed) params.feed_id = selectedFeed;
    Promise.all([
      api.articles.browse(params),
      api.feeds.list(),
    ])
      .then(([browseResult, feedsList]) => {
        setArticles(browseResult.articles);
        // Only show manual feeds in filter
        setFeeds(feedsList.filter(f => f.import_mode === "manual"));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedFeed]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleSelect(id: number) {
    setActionMap(m => ({ ...m, [id]: "selecting" }));
    try {
      await api.articles.select(id);
      setActionMap(m => ({ ...m, [id]: "done" }));
      // Remove from browse list
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      alert(`Error selecting article: ${String(e)}`);
      setActionMap(m => { const n = { ...m }; delete n[id]; return n; });
    }
  }

  async function handleDismiss(id: number) {
    const note = prompt("Dismiss note (optional):");
    if (note === null) return; // user cancelled
    setActionMap(m => ({ ...m, [id]: "dismissing" }));
    try {
      await api.articles.dismiss(id, note);
      setArticles(prev => prev.filter(a => a.id !== id));
    } catch (e) {
      alert(`Error dismissing: ${String(e)}`);
      setActionMap(m => { const n = { ...m }; delete n[id]; return n; });
    }
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Feed Browse View</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manual feeds — select articles to import, dismiss to permanently remove
          </p>
        </div>
        <button onClick={loadData} className="btn-ghost btn btn-sm">
          Refresh
        </button>
      </div>

      {/* Feed filter */}
      {feeds.length > 0 && (
        <div className="mb-5">
          <select
            value={selectedFeed}
            onChange={e => setSelectedFeed(e.target.value)}
            className="input max-w-xs text-sm"
          >
            <option value="">All manual feeds</option>
            {feeds.map(f => (
              <option key={f.id} value={String(f.id)}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-400 text-sm">No new articles in browse view</p>
          <p className="text-gray-400 text-xs mt-1">
            Auto-import feeds process automatically. Manual feeds appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => {
            const action = actionMap[article.id];
            const busy = action === "selecting" || action === "dismissing";

            return (
              <div key={article.id} className="card p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Meta row */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="badge-gray text-xs">{article.feed_name}</span>
                      <span className="badge-gray text-xs">{article.source_type}</span>
                      {article.original_author && (
                        <span className="text-gray-400 text-xs">{article.original_author}</span>
                      )}
                      <span className="text-gray-400 text-xs">{relativeTime(article.original_published_at ?? article.created_at)}</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-medium text-gray-900 leading-snug">
                      {article.original_title}
                    </h3>

                    {/* Excerpt */}
                    {article.original_body && (
                      <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                        {truncate(article.original_body, 240)}
                      </p>
                    )}

                    {/* URL */}
                    <a
                      href={article.original_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-500 hover:underline mt-1 block truncate"
                    >
                      {article.original_url}
                    </a>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleSelect(article.id)}
                      disabled={busy}
                      className="btn btn-primary btn-sm disabled:opacity-50"
                    >
                      {action === "selecting" ? "Importing…" : "Import"}
                    </button>
                    <button
                      onClick={() => handleDismiss(article.id)}
                      disabled={busy}
                      className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {action === "dismissing" ? "Dismissing…" : "Dismiss"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
